import {AfterViewInit, ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {Validators} from '@angular/forms';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormBuilder} from '@aviary/forms/extensions/extended-form-builder/extended-form-builder';
import {CurrencyFormFieldComponent} from '@aviary/forms/fields/currency-form-field/currency-form-field.component';
import {CommonValidators} from '@aviary/store/common/common.validators';
import {MakeAPaymentValue, SquareAppDetails} from '@store/balance/balance.types';
import {SquareCardToken, SquarePaymentService} from './square-payment.service';
import {AsyncPipe} from '@angular/common';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {PersistentFormDialog, PersistentFormDialogDefinition} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.types';
import {PersistentFormDialogComponent} from '@aviary/dialogs/persistent-form-dialog/persistent-form-dialog.component';
import {Observable} from 'rxjs';

export interface MakeAPaymentDialogData {
  squareAppDetails: SquareAppDetails;
  currentAccountBalance: number;
  minimumAmount: number;
}

@Component({
  selector: 'px-make-a-payment',
  template: `
    @let loading = (squareService.ready | async) !== true;

    <mx-persistent-form-dialog [definition]="dialogDefinition" [submissionEnabled]="!loading">
      <div class="flex justify-end">
        <mx-currency-form-field fieldName="amount" [formGroup]="paymentForm" class="max-w-[9.25rem]"/>
      </div>
      <div id="card-container" class="min-h-[92px]"></div>
      <div class="w-full h-8 flex items-center place-content-center">
        @if (loading) {
          <mat-spinner [diameter]="32"/>
        }
      </div>
    </mx-persistent-form-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [SquarePaymentService],
  imports: [CurrencyFormFieldComponent, PersistentFormDialogComponent, AsyncPipe, MatProgressSpinner]
})
export class MakeAPaymentDialog implements AfterViewInit, PersistentFormDialog<MakeAPaymentDialogData, MakeAPaymentValue> {
  public readonly dialogData: MakeAPaymentDialogData = inject(MAT_DIALOG_DATA);
  public readonly currentAccountBalance: number = this.dialogData.currentAccountBalance;
  public readonly squareService = inject(SquarePaymentService);
  public readonly paymentForm: ExtendedFormGroup = inject(ExtendedFormBuilder).nonNullable.extendedGroup({
    amount: [
      this.currentAccountBalance,
      {
        validators: [Validators.required, CommonValidators.minPayment(this.dialogData.minimumAmount), CommonValidators.currencyMax(this.currentAccountBalance)]
      }
    ],
  });
  public readonly dialogDefinition: PersistentFormDialogDefinition<MakeAPaymentValue> = {
    title: 'Make A Payment',
    confirmButtonLabel: 'Submit',
    formGroup: this.paymentForm,
    allowConfirmWhenPristine: true,
    showConfirmationPendingSpinner: false
  };

  public ngAfterViewInit() {
    this.squareService.setSquareAppDetails(this.dialogData.squareAppDetails);
    this.squareService.loadSquareScript();
  }

  public tokenizeCard(): Observable<SquareCardToken> {
    return this.squareService.submit();
  }
}
